import { useState } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { Container, IconButton } from '@mui/material';
import { enqueueSnackbar } from 'notistack';

import useMerchant from 'src/hooks/useMerchant';
import Page from 'src/components/Page';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import { getProgramMenu } from './dashboard/navbar/NavConfig';
import ProgramTopNavWrapper from './dashboard/navbar/ProgramTopNavWrapper';
import ProgrammeRenameDialog from './dashboard/dialogs/ProgrammeRenameDialog';
import { updateMerchantProgrammeApi } from 'src/api/merchantApi';
import useCarbonUser from 'src/hooks/useCarbonUser';
import { readableStringFormatter } from 'src/utils/stringFormatter';
import { StackColumn } from 'src/components';
import { TimezoneDescriptor } from 'src/components/date-range-picker/timezone-descriptor';
import { IconPencil } from 'src/libraries/icons';

//
//

const ProgramsLayout: React.FC<React.PropsWithChildren<{ currentPath?: string }>> = ({ currentPath, children }) => {
  const { id, type } = useParams()
  const { merchant, isMerchantLoading, getMerchant, updatePortalConfig } = useMerchant();
  const { carbonUser } = useCarbonUser();
  const { pathname } = useLocation();
  const [isRenameModalOpen,setRenameModalOpen] = useState(false);
  const [programmeRenameLoading,setProgrammeRenameLoading] = useState(false);

  if (pathname.includes('transaction') && id) return <Outlet />

  // const isEmployeeEngagementPage = pathname.includes('employee') && pathname.includes('engagement');
  // const hideDatePicker = !isMerchantLoading && isEmployeeEngagementPage;
  
  const isEmployeesPage = pathname.includes('employees');
  const isEmissionsPage = pathname.includes('emissions');
  const isEmployeeEngagementPage = pathname.includes('employee') && pathname.includes('engagement');
  const isConfigurationPage = pathname.includes('configuration');

  const menuItems = merchant?.programmes && getProgramMenu(merchant.programmes);
  const currentOpenedProgram = menuItems?.find((el) => pathname?.includes(el?.path));
  const exactPathMatch = currentOpenedProgram?.programmeMenu?.find((el) => el?.path === pathname);
  const currentLocation = exactPathMatch ||
      currentOpenedProgram?.programmeMenu
        ?.filter((el) => el.isDynamicPath)
        .sort((el1, el2) => el1.path.length - el2.path.length)
        .find((el) => pathname?.match(el?.path));

  const showDatePicker = !isMerchantLoading && !isEmissionsPage && !isEmployeesPage && !isEmployeeEngagementPage && !isConfigurationPage && !currentLocation?.hideDatePicker && !currentOpenedProgram?.hideDatePicker;

  const pageTitle = currentOpenedProgram?.title || 'Loading...';

  const getBreadcrumbs = (currentPath?: string, root?: string) => {
    const baseProgramLinks: { title: string, links: Array<{name: string, href?: string}>} = {
      title: readableStringFormatter(pageTitle),
      links: [
        { name: 'Programmes' },
        { name: readableStringFormatter(type) },
        { name: currentLocation?.title || 'Loading...' },
      ]
    };

    if (currentPath && root) {
      return { title: readableStringFormatter(currentPath), links: [{ name: root }, { name: currentPath }] };
    } else {
      return baseProgramLinks;
    }
  };

  const breadcrumbs = getBreadcrumbs(currentPath, 'Programmes');
  const programmeName = breadcrumbs.title;

  const onUpdateMerchantProgrammeName = async (newName: string) => {
    setProgrammeRenameLoading(true);
    try {
      if (merchant && currentOpenedProgram) {
        enqueueSnackbar("Please wait while we update your portal...", { variant: 'info' });
        await updateMerchantProgrammeApi(merchant.code, currentOpenedProgram?.code, newName)
        enqueueSnackbar("Refreshing your programmes...", { variant: 'info' });
        await getMerchant(carbonUser, merchant.id);
        enqueueSnackbar("Updating the sidebar...", { variant: 'info' });
        try {
          await updatePortalConfig();
          enqueueSnackbar("Programme name was successfully updated!", { variant: 'success' });
        } catch (e) {
          console.error(e);
          enqueueSnackbar("Programme name was successfully updated. However, you might need to refresh you page to see the changes.", { variant: 'warning' });
        }
      } else {
        enqueueSnackbar("Please wait for the content to fully load", { variant: 'warning' });
      }
    } catch (e) {
      console.error('Update programme name', e)
    } finally {
      setProgrammeRenameLoading(false);
    }
  }
  return (
    <Page title={programmeName} titleActions={
      currentOpenedProgram?.code && !(['EVENT','CUSTOMER','BUSINESS'].includes(currentOpenedProgram.code)) && 
        (<IconButton disabled={isMerchantLoading || programmeRenameLoading} onClick={() => setRenameModalOpen(true)}>
          <IconPencil size={18} />
        </IconButton>) || undefined
    }>
      <Container maxWidth={false}>
        <HeaderBreadcrumbs links={breadcrumbs.links} />

        <StackColumn>
          <ProgramTopNavWrapper
            navigation={currentOpenedProgram?.programmeMenu}
            showDatePicker={showDatePicker}
          />

          <Outlet />

          <TimezoneDescriptor />
        </StackColumn>
      </Container>
      <ProgrammeRenameDialog 
        modalOpen={isRenameModalOpen}
        setModalOpen={setRenameModalOpen}
        currentName={programmeName}
        onRename={onUpdateMerchantProgrammeName}
      />
    </Page>
  );
};

export default ProgramsLayout;
